import React, { useState, useMemo, useCallback } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { observer } from "mobx-react-lite";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  HStack,
  useToast,
  Collapse
} from "@chakra-ui/react";
import { useStores } from "../../store/store_context";
import ColumnFilters from "./column_filters";
import { PAYMENT_DETAILS_KEYS, paymentTextAttributes } from "./payment_columns";
import DealLink from "../deal_link";
import { createPaymentsWorkbook } from "./payment_export";
import { NO_DATE_KEY, periodKeyOf, sortPeriodKeys } from "./payment_period";

const MONTH_NAMES = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"
];

const TableData = observer(({ data, renderButtons }) => {
  const toast = useToast();
  const { pageStore } = useStores();
  const [expandedGroups, setExpandedGroups] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: 'Sum',
    direction: 'desc',
    level: 'pivot'
  });

  const isManagerLiteView = pageStore.selected_view === "manager-lite" || pageStore.selected_view === "manager-partner-lite";

  const availableColumns = useMemo(() => {
    const filtered = pageStore.columnConfigPayments.filter(
      col => (data.length > 0 && data[0].hasOwnProperty(col.key) && col.active) || (col.active && ["RightTotal", "RightAvgTotal", "LeftTotal", "LeftAvgTotal"])
    );

    return filtered.map(col => ({
      ...col,
      isNumeric: col.key === "Sum"
    }));
  }, [data, pageStore.columnConfigPayments]);

  // Keep each existing layout intact; the three new fields are also available
  // in compact views, in the same payment rows revealed by the existing '+'.
  const detailColumns = availableColumns.filter(column =>
    !["Sum", "RightTotal", "RightAvgTotal", "LeftTotal", "LeftAvgTotal"].includes(column.key) &&
    (!isManagerLiteView || PAYMENT_DETAILS_KEYS.includes(column.key))
  );

  const handleSort = useCallback((key, level) => {
    setSortConfig(prev => {
      if (prev.key === key && prev.level === level) {
        return {
          key,
          level,
          direction: prev.direction === "asc" ? "desc" : "asc"
        };
      }
      return { key, level, direction: "desc" };
    });
  }, []);

  const formatCellValue = useCallback((value, isNumeric = false, asText = false) => {
    if (value == null) return "";
    if (asText) return String(value);

    if (typeof value === "number" && isNumeric) {
      return Math.round(value).toLocaleString("ru-RU");
    }

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-");
      return `${day}.${month}.${year}`;
    }

    return value || "";
  }, []);

  // Столбец «ИНН / УНП» берёт значение как есть из строки оплаты 1С (поле Inn):
  // у российских контрагентов там ИНН, у белорусских — УНП. Подстановки из
  // карточки компании Б24 здесь больше нет — реквизит только из 1С.
  const cellValue = useCallback((column, row) => row[column.key], []);

  // ID сделки выводим ссылкой на карточку Битрикса; остальные ячейки без изменений.
  const renderCellValue = useCallback((column, value) => {
    if (column.key === "IdDeal" && value) {
      return <DealLink id={value} />;
    }
    return formatCellValue(value, column.isNumeric, column.exportAsText);
  }, [formatCellValue]);

  const formatDateKey = useCallback((key) => {
    if (key === NO_DATE_KEY) return key;

    if (pageStore.year_or_mouth_view === "month") {
      const [year, month] = key.split("-");
      return `${MONTH_NAMES[parseInt(month) - 1]} ${year}`;
    }

    return `${key}`;
  }, [pageStore.year_or_mouth_view]);

  const { groupedData, allTimeKeys, totalSums, totalGlobalSum } = useMemo(() => {

    if (pageStore.selected_view === "deal") {
      setSortConfig({ key: "DateDoc", direction: "desc", level: "detail" })
    }

    if (!pageStore.selected_view || !pageStore.year_or_mouth_view) {
      return { groupedData: null, allTimeKeys: [], totalSums: {}, totalGlobalSum: 0 };
    }

    const getPrimaryKey = (item) => {
      const keyMap = {
        "manager-lite": item.Manager,
        "manager-deals": item.Manager,
        "manager-orgs": item.Manager,
        "org-deals": item.Org,
        "manager-partner": item.Manager,
        "partner-manager": item.Partner,
        "partner": item.Partner,
        "manager-partner-lite": item.Manager,
        "departament-manager": item.departament_name,
        "departament-manager-withoutROP": item.departament_name
      };
      return keyMap[pageStore.selected_view] || "Прочее";
    };

    const getSecondaryKey = (item) => {
      const keyMap = {
        "manager-orgs": item.Org,
        "manager-partner": item.Partner,
        "partner-manager": item.Manager,
        "manager-partner-lite": item.Partner,
        "departament-manager": item.Manager,
        "departament-manager-withoutROP": item.Manager
      };
      return keyMap[pageStore.selected_view] || null;
    };

    const grouped = {};
    const timeKeysSet = new Set();
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // 1–12

    // ✅ Источник дат (день или месяц)
    const startDateRaw = pageStore.start_date_day || pageStore.start_date_month || pageStore.start_date_year;
    const endDateRaw = pageStore.end_date_day || pageStore.end_date_month || pageStore.end_date_year;

    // ✅ Если даты не заданы — берём всё до текущего месяца
    const startDate = startDateRaw
      ? new Date(startDateRaw)
      : new Date(2020, 0, 1); // 2020-01-01 (можешь поменять)

    const endDate = endDateRaw
      ? new Date(endDateRaw)
      : new Date(currentYear, currentMonth - 1, 1);

    // ✅ Получаем диапазон годов
    function getYearsInRange() {
      const years = [];
      for (let y = startDate.getFullYear(); y <= endDate.getFullYear(); y++) {
        years.push(y);
      }
      return years;
    }

    // ✅ Получаем диапазон месяцев для конкретного года
    function getMonthsForYear(year) {
      let start = 1;
      let end = 12;

      if (year === startDate.getFullYear()) {
        start = startDate.getMonth() + 1;
      }

      if (year === endDate.getFullYear()) {
        end = endDate.getMonth() + 1;
      }

      const months = [];
      for (let m = start; m <= end; m++) {
        // добавляем ведущий ноль
        months.push(String(m).padStart(2, "0"));
      }

      return months;
    }

    // ✅ ГОДОВОЙ ВИД
    if (pageStore.year_or_mouth_view === "year") {
      const years = getYearsInRange();

      years.forEach(year => {
        timeKeysSet.add(String(year));
      });
    }

    // ✅ МЕСЯЧНЫЙ ВИД
    if (pageStore.year_or_mouth_view === "month") {
      const years = getYearsInRange();

      years.forEach(year => {
        const months = getMonthsForYear(year);

        months.forEach(month => {
          const title = `${year}-${month.padStart(2, "0")}`;
          timeKeysSet.add(title);
        });
      });
    }


    data.forEach(item => {
      const primaryKey = getPrimaryKey(item);
      const secondaryKey = getSecondaryKey(item) || "Без подгруппы";

      if ((pageStore.selected_view == "departament-manager-withoutROP") && (item.role_name == "Руководитель отдела продаж" || item.role_name == "Руководитель удалённого отдела продаж")) {
        return
      };

      if (pageStore.workers && !item.active) return

      const timeKey = periodKeyOf(item.DateDoc, pageStore.year_or_mouth_view);

      // Платёж без даты документа получает колонку «Без даты» — но только если
      // за ним стоят деньги. Иначе колонка из одних нулей появлялась бы из-за
      // строк-заглушек менеджеров без платежей. Суть та же: сумма не должна
      // попадать в «Итого», не будучи видимой ни в одном периоде.
      if (timeKey !== NO_DATE_KEY || item.Sum) timeKeysSet.add(timeKey);

      if (!grouped[primaryKey]) {
        grouped[primaryKey] = {
          timeGroups: {},
          totalSum: 0,
          subGroups: {},
        };
      }

      grouped[primaryKey].timeGroups[timeKey] =
        (grouped[primaryKey].timeGroups[timeKey] || 0) + item.Sum;
      grouped[primaryKey].totalSum += item.Sum;

      if (!grouped[primaryKey].subGroups[secondaryKey]) {
        grouped[primaryKey].subGroups[secondaryKey] = {
          items: [],
          timeGroups: {},
          totalSum: 0,
        };
      }

      const subGroup = grouped[primaryKey].subGroups[secondaryKey];
      subGroup.items.push(item);
      subGroup.timeGroups[timeKey] = subGroup.timeGroups[timeKey] || { items: [], sum: 0 };
      subGroup.timeGroups[timeKey].items.push(item);
      subGroup.timeGroups[timeKey].sum += item.Sum;
      subGroup.totalSum += item.Sum;
    });


    const allTimeKeys = sortPeriodKeys(new Set(timeKeysSet));


    const groupedArray = Object.entries(grouped).map(([primaryKey, group]) => {
      const subGroups = Object.entries(group.subGroups).map(([secondaryKey, subGroup]) => ({
        secondaryKey,
        ...subGroup
      }));

      const hasOnlyDefaultSubgroup = subGroups.length === 1 && subGroups[0].secondaryKey === "Без подгруппы";

      return {
        primaryKey,
        timeGroups: group.timeGroups,
        totalSum: group.totalSum,
        avgSum: allTimeKeys.length > 0 ? group.totalSum / allTimeKeys.length : 0,
        subGroups: hasOnlyDefaultSubgroup
          ? [{ ...subGroups[0], secondaryKey: null, autoExpanded: true }]
          : subGroups
      };
    });

    const totalSums = {};
    let totalGlobalSum = 0;

    groupedArray.forEach(group => {
      totalGlobalSum += group.totalSum;
      Object.entries(group.timeGroups).forEach(([timeKey, sum]) => {
        totalSums[timeKey] = (totalSums[timeKey] || 0) + sum;
      });
    });

    return {
      groupedData: groupedArray,
      allTimeKeys,
      totalSums,
      totalGlobalSum
    };
  }, [data, pageStore.selected_view, pageStore.year_or_mouth_view, pageStore.columnConfigPayments, pageStore.workers]);

  const getSortValue = useCallback((item, key, level) => {
    if (level === 'pivot') {
      switch (key) {
        case 'group-name':
          return item.primaryKey || item.secondaryKey || '';
        case 'group-total':
        case 'Sum':
          return item.totalSum || 0;
        case 'group-avg':
          return item.avgSum !== undefined ? item.avgSum :
            (allTimeKeys.length > 0 ? (item.totalSum || 0) / allTimeKeys.length : 0);
        default:
          if (key.startsWith('period||')) {
            const periodKey = key.split('||');
            if (!item.timeGroups) return 0;
            const periodValue = item.timeGroups[periodKey[2]];
            if (!periodValue || periodValue == 0) return -99999999;
            const prevKey = allTimeKeys[allTimeKeys.indexOf(periodKey[2]) - 1]
            const prevPeriodValue = item.timeGroups[prevKey]
            if (typeof periodValue === 'object' && periodValue !== null) {
              if (periodKey[1] == 'abs') return periodValue.sum || 0;
              if (periodKey[1] == 'percent') return prevPeriodValue && prevPeriodValue.sum && periodValue.sum && periodValue.sum != 0 ? (periodValue.sum - prevPeriodValue?.sum) / prevPeriodValue?.sum : 0;
              if (periodKey[1] == 'part') return periodValue.sum / (totalSums[periodKey[2]]) * 100
            } else {
              if (periodKey[1] == 'abs') return periodValue || 0;
              if (periodKey[1] == 'percent') return prevPeriodValue && periodValue && periodValue != 0 ? (periodValue - prevPeriodValue) / prevPeriodValue : 0;
              if (periodKey[1] == 'part') return periodValue / (totalSums[periodKey[2]]) * 100
            }
          }
          return 0;
      }
    } else {
      if (key.startsWith('period||')) {
        const periodKey = key.split('||');
        const date = new Date(item.DateDoc);
        let itemPeriodKey;

        if (date && pageStore.year_or_mouth_view === "month") {
          itemPeriodKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
        } else if (date) {
          itemPeriodKey = `${date.getFullYear()}`;
        }
        if (itemPeriodKey != periodKey[2]) return 0
        if (periodKey[1] == 'abs') return item.Sum
        if (periodKey[1] == 'percent') return item.Sum
        if (periodKey[1] == 'part') return item.Sum / (totalSums[periodKey[2]] || 0) * 100
      } else {
        const value = item[key];
        const column = availableColumns.find(c => c.key === key);

        if (column?.isNumeric) {
          return parseFloat(value) || 0;
        }
        return value || "";
      }
    }
  }, [allTimeKeys.length, pageStore.year_or_mouth_view, availableColumns]);

  const sortedGroupedData = useMemo(() => {
    if (!groupedData) return null;

    const compareItems = (a, b, key, level) => {
      const valueA = getSortValue(a, key, level);
      const valueB = getSortValue(b, key, level);

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return sortConfig.direction === 'asc' ? valueA - valueB : valueB - valueA;
      }

      return sortConfig.direction === 'asc'
        ? String(valueA).localeCompare(String(valueB))
        : String(valueB).localeCompare(String(valueA));
    };

    const sortedGroups = [...groupedData].sort((a, b) =>
      compareItems(a, b, sortConfig.key, 'pivot'));

    return sortedGroups.map(group => ({
      ...group,
      subGroups: group.subGroups
        .sort((a, b) => compareItems(a, b, sortConfig.key, 'pivot'))
        .map(subGroup => ({
          ...subGroup,
          items: [...subGroup.items].sort((a, b) =>
            compareItems(a, b, sortConfig.key, 'detail'))
        }))
    }));
  }, [groupedData, sortConfig, getSortValue, pageStore.rowHeight]);

  const handleExportToXLSX = useCallback(async () => {
    var can = await pageStore.CheckPermission("export excel")
    console.log("export:")
    console.log(can)
    if (!can) {
      toast({
        title: "У вас нет права экспорта в excel",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return
    }
    const table = document.querySelector("table");
    if (!table) return;

    const workbook = createPaymentsWorkbook(table);

    // Сохраняем файл
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "table.xlsx");
  }, [groupedData, allTimeKeys, totalSums, totalGlobalSum, availableColumns, formatCellValue, formatDateKey, isManagerLiteView]);

  const toggleExpand = useCallback((type, key, subKey = null) => {
    const compositeKey = subKey ? `${key}__${subKey}` : key;
    setExpandedGroups(prev => ({
      ...prev,
      [compositeKey]: !prev[compositeKey]
    }));
  }, []);

  const expandAll = useCallback(() => {
    const newExpanded = {};
    groupedData?.forEach(group => {
      newExpanded[group.primaryKey] = true;
      group.subGroups.forEach(subGroup => {
        newExpanded[`${group.primaryKey}__${subGroup.secondaryKey ?? "auto"}`] = true;
      });
    });
    setExpandedGroups(newExpanded);
  }, [groupedData]);

  const collapseAll = useCallback(() => {
    setExpandedGroups({});
  }, []);

  const renderColumns = () =>
    pageStore.year_or_mouth_view &&
    allTimeKeys.map(timeKey => (
      <React.Fragment key={timeKey}>
        {pageStore.absolute_column_payments && (
          <Th
            color="white"
            textAlign="right"
            onClick={() => handleSort(`period||abs||${timeKey}`, 'pivot')}
            cursor="pointer"
          >
            {formatDateKey(timeKey)}
            {sortConfig.key === `period||abs||${timeKey}` &&
              sortConfig.level === 'pivot' && (
                <Box as="span" ml={2}>
                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                </Box>
              )}
          </Th>
        )}

        {pageStore.percent_column_payments && (
          <Th
            color="white"
            textAlign="right"
            onClick={() => handleSort(`period||percent||${timeKey}`, 'pivot')}
            cursor="pointer"
          >
            {pageStore.absolute_column_payments
              ? "%"
              : `${formatDateKey(timeKey)} %`}
            {sortConfig.key === `period||percent||${timeKey}` &&
              sortConfig.level === 'pivot' && (
                <Box as="span" ml={2}>
                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                </Box>
              )}
          </Th>
        )}

        {pageStore.part_column_payments && (
          <Th
            color="white"
            textAlign="right"
            onClick={() => handleSort(`period||part||${timeKey}`, 'pivot')}
            cursor="pointer"
          >
            Доли, %
            {sortConfig.key === `period||part||${timeKey}` &&
              sortConfig.level === 'pivot' && (
                <Box as="span" ml={2}>
                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                </Box>
              )}
          </Th>
        )}
      </React.Fragment>
    ));

  const renderTableHeader = () => (
    <Thead bgColor={"black"} position={"sticky"} top={"0px"} zIndex={11}>
      <Tr>
        <Th color="white" px={0} />
        <Th
          color="white"
          onClick={() => handleSort('group-name', 'pivot')}
          cursor="pointer"
        >
          Группа
          {sortConfig.key === 'group-name' && sortConfig.level === 'pivot' && (
            <Box as="span" ml={2}>
              {sortConfig.direction === "asc" ? "↑" : "↓"}
            </Box>
          )}
        </Th>
        {availableColumns.map(item => item.key).includes("LeftTotal") ?
          <Th
            color="white"
            textAlign="right"
            onClick={() => handleSort('Sum', 'pivot')}
            cursor="pointer"
          >
            Итого
            {sortConfig.key === 'Sum' && sortConfig.level === 'pivot' && (
              <Box as="span" ml={2}>
                {sortConfig.direction === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Th>
          : <></>}
        {availableColumns.map(item => item.key).includes("LeftAvgTotal") ?
          <Th
            color="white"
            textAlign="right"
            onClick={() => handleSort('group-avg', 'pivot')}
            cursor="pointer"
          >
            Итого среднее
            {sortConfig.key === 'group-avg' && sortConfig.level === 'pivot' && (
              <Box as="span" ml={2}>
                {sortConfig.direction === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Th>
          : <></>}
        {detailColumns
          .map(column => (
            <Th
              key={column.key}
              color="white"
              px={4}
              py={2}
              textAlign={column.isNumeric ? "right" : "left"}
              onClick={() => handleSort(column.key, 'detail')}
              cursor="pointer"
            >
              {column.title}
              {sortConfig.key === column.key && sortConfig.level === 'detail' && (
                <Box as="span" ml={2}>
                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                </Box>
              )}
            </Th>
          ))}
        {renderColumns()}
        {availableColumns.map(item => item.key).includes("RightTotal") ?
          <Th
            color="white"
            textAlign="right"
            onClick={() => handleSort('Sum', 'pivot')}
            cursor="pointer"
          >
            Итого
            {sortConfig.key === 'Sum' && sortConfig.level === 'pivot' && (
              <Box as="span" ml={2}>
                {sortConfig.direction === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Th>
          : <></>}
        {availableColumns.map(item => item.key).includes("RightAvgTotal") ?
          <Th
            color="white"
            textAlign="right"
            onClick={() => handleSort('group-avg', 'pivot')}
            cursor="pointer"
          >
            Итого среднее
            {sortConfig.key === 'group-avg' && sortConfig.level === 'pivot' && (
              <Box as="span" ml={2}>
                {sortConfig.direction === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Th>
          : <></>}
      </Tr>
    </Thead>
  );

  const renderTotalRow = () => (
    <Tr bg="#555">
      <Td px={0} py={0} />
      <Td fontWeight="bold" color="white">Итого {data.length}</Td>
      {availableColumns.map(item => item.key).includes("LeftTotal") ?
        <Td textAlign="right" fontWeight="bold" color="rgb(100,240,10)" py={pageStore.rowHeight}>
          {formatCellValue(totalGlobalSum, true)}
        </Td>
        : <></>}
      {availableColumns.map(item => item.key).includes("LeftAvgTotal") ?
        <Td textAlign="right" fontWeight="bold" color="rgb(15, 175, 250)" py={pageStore.rowHeight}>
          {formatCellValue(allTimeKeys.length > 0 ? totalGlobalSum / allTimeKeys.length : 0, true)}
        </Td>
        : <></>}
      {detailColumns
        .map(column => (
          <Th key={column.key} color="white" px={4} py={2} textAlign={column.isNumeric ? "right" : "left"} />
        ))}
      {pageStore.year_or_mouth_view &&
        allTimeKeys.map((timeKey, i) => {
          const current = totalSums[timeKey] ?? 0;
          const prevKey = allTimeKeys[i - 1];
          const prev = prevKey ? totalSums[prevKey] ?? 0 : 0;

          return (
            <React.Fragment key={timeKey}>
              {pageStore.absolute_column_payments && (
                <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                  {formatCellValue(current, true)}
                </Td>
              )}

              {pageStore.percent_column_payments && (() => {
                if (prev === 0) {
                  return (
                    <Td textAlign="right" fontWeight="bold" color="gray.400" py={pageStore.rowHeight}>
                      —
                    </Td>
                  );
                }

                const ratio = (current - prev) / prev; // относительное изменение
                const percent = ratio * 100;

                let color = "white";
                let sign = "";

                if (ratio < 0) {
                  color = "red.400";
                  sign = "−";
                } else if (ratio < 0.2) {
                  color = "yellow.400";
                  sign = "+";
                } else {
                  color = "green.300"; // яркосалатовый
                  sign = "+";
                }

                return (
                  <Td textAlign="right" fontWeight="bold" color={color} py={pageStore.rowHeight}>
                    {sign}
                    {formatCellValue(Math.abs(percent), true)}
                  </Td>
                );
              })()}

              {pageStore.part_column_payments && (
                <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                  100%
                </Td>
              )}
            </React.Fragment>
          );
        })}
      {availableColumns.map(item => item.key).includes("RightTotal") ?
        <Td textAlign="right" fontWeight="bold" color="rgb(100,240,10)" py={pageStore.rowHeight}>
          {formatCellValue(totalGlobalSum, true)}
        </Td>
        : <></>}
      {availableColumns.map(item => item.key).includes("RightAvgTotal") ?
        <Td textAlign="right" fontWeight="bold" color="rgb(15, 175, 250)" py={pageStore.rowHeight}>
          {formatCellValue(allTimeKeys.length > 0 ? totalGlobalSum / allTimeKeys.length : 0, true)}
        </Td>
        : <></>}
    </Tr>
  );

  const renderGroupRow = (group) => {
    const isExpanded = expandedGroups[group.primaryKey];

    return (
      <React.Fragment key={group.primaryKey}>
        <Tr bg="#333">
          <Td px={0} py={0}>
            <Button
              size="sm"
              fontSize="20px"
              bg="#333"
              color="white"
              _hover={{ bg: "#222" }}
              onClick={() => toggleExpand('group', group.primaryKey)}
            >
              {isExpanded ? "−" : "+"}
            </Button>
          </Td>

          <Td
            cursor="pointer"
            onClick={() => toggleExpand('group', group.primaryKey)}
            fontWeight="bold"
            color="white"
            py={pageStore.rowHeight}
          >
            {group.primaryKey}
          </Td>
          {availableColumns.map(item => item.key).includes("LeftTotal") ?
            <Td textAlign="right" fontWeight="bold" color="rgb(100,240,10)" py={pageStore.rowHeight}>
              {formatCellValue(group.totalSum, true)}
            </Td>
            : <></>}
          {availableColumns.map(item => item.key).includes("LeftAvgTotal") ?
            <Td textAlign="right" fontWeight="bold" color="rgb(15, 175, 250)" py={pageStore.rowHeight}>
              {formatCellValue(group.avgSum, true)}
            </Td>
            : <></>}
          {detailColumns
            .map(column => (
              <Th
                key={column.key}
                color="white"
                px={4}
                py={2}
                textAlign={column.isNumeric ? "right" : "left"}
              />
            ))}
          {pageStore.year_or_mouth_view &&
            allTimeKeys.map((timeKey, i) => {
              const current = group.timeGroups[timeKey] ?? 0;
              const prevKey = allTimeKeys[i - 1];
              const prev = prevKey ? group.timeGroups[prevKey] ?? 0 : 0;

              return (
                <React.Fragment key={timeKey}>
                  {pageStore.absolute_column_payments && (
                    <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                      {formatCellValue(current, true)}
                    </Td>
                  )}

                  {pageStore.percent_column_payments && (() => {
                    if (prev === 0) {
                      return (
                        <Td textAlign="right" fontWeight="bold" color="gray.400" py={pageStore.rowHeight}>
                          —
                        </Td>
                      );
                    }

                    const ratio = (current - prev) / prev; // относительное изменение
                    const percent = ratio * 100;

                    let color = "white";
                    let sign = "";

                    if (ratio < 0) {
                      color = "red.400";
                      sign = "−";
                    } else if (ratio < 0.2) {
                      color = "yellow.400";
                      sign = "+";
                    } else {
                      color = "green.300"; // яркосалатовый
                      sign = "+";
                    }

                    return (
                      <Td textAlign="right" fontWeight="bold" color={color} py={pageStore.rowHeight}>
                        {sign}
                        {formatCellValue(Math.abs(percent), true)}
                      </Td>
                    );
                  })()}

                  {pageStore.part_column_payments && (
                    <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                      {formatCellValue(current / (totalSums[timeKey] || 0) * 100, true)}
                    </Td>
                  )}
                </React.Fragment>
              );
            })}
          {availableColumns.map(item => item.key).includes("RightTotal") ?
            <Td textAlign="right" fontWeight="bold" color="rgb(100,240,10)" py={pageStore.rowHeight}>
              {formatCellValue(group.totalSum, true)}
            </Td>
            : <></>}
          {availableColumns.map(item => item.key).includes("RightAvgTotal") ?
            <Td textAlign="right" fontWeight="bold" color="rgb(15, 175, 250)" py={pageStore.rowHeight}>
              {formatCellValue(group.avgSum, true)}
            </Td>
            : <></>}
        </Tr>

        {isExpanded && group.subGroups.map(renderSubGroupRow(group))}
      </React.Fragment>
    );
  };

  const renderSubGroupRow = (group) => (subGroup) => {
    const compositeKey = `${group.primaryKey}__${subGroup.secondaryKey ?? "auto"}`;
    const isExpanded = expandedGroups[compositeKey] || subGroup.autoExpanded;
    const subGroupAvg = allTimeKeys.length > 0 ? subGroup.totalSum / allTimeKeys.length : 0;

    return (
      <React.Fragment key={compositeKey}>
        {subGroup.secondaryKey && (
          <Tr bg="#444">
            <Td px={0} py={0}>
              <Button
                color="white"
                fontSize="25px"
                _hover={{ bg: "black", color: "white" }}
                size="sm"
                bg="#444"
                onClick={() => toggleExpand('subgroup', group.primaryKey, subGroup.secondaryKey)}
              >
                {isExpanded ? "−" : "+"}
              </Button>
            </Td>
            <Td
              pl={12}
              cursor="pointer"
              onClick={() => toggleExpand('subgroup', group.primaryKey, subGroup.secondaryKey)}
              py={pageStore.rowHeight}
              fontWeight="bold"
            >
              {subGroup.secondaryKey}
            </Td>
            {availableColumns.map(item => item.key).includes("LeftTotal") ?
              <Td textAlign="right" fontWeight="bold" py={pageStore.rowHeight}>
                {formatCellValue(subGroup.totalSum, true)}
              </Td>
              : <></>}
            {availableColumns.map(item => item.key).includes("LeftAvgTotal") ?
              <Td textAlign="right" py={pageStore.rowHeight}>
                {formatCellValue(subGroupAvg, true)}
              </Td>
              : <></>}
            {detailColumns
              .map(column => (
                <Th
                  key={column.key}
                  color="white"
                  px={4}
                  py={2}
                  textAlign={column.isNumeric ? "right" : "left"}
                />
              ))}
            {pageStore.year_or_mouth_view &&
              allTimeKeys.map((timeKey, i) => {
                const current = subGroup.timeGroups[timeKey]?.sum ?? 0;
                const prevKey = allTimeKeys[i - 1];
                const prev = prevKey ? subGroup.timeGroups[prevKey]?.sum ?? 0 : 0;

                return (
                  <React.Fragment key={timeKey}>
                    {pageStore.absolute_column_payments && (
                      <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                        {formatCellValue(current, true)}
                      </Td>
                    )}

                    {pageStore.percent_column_payments && (() => {
                      if (prev === 0) {
                        return (
                          <Td textAlign="right" fontWeight="bold" color="gray.400" py={pageStore.rowHeight}>
                            —
                          </Td>
                        );
                      }

                      const ratio = (current - prev) / prev; // относительное изменение
                      const percent = ratio * 100;

                      let color = "white";
                      let sign = "";

                      if (ratio < 0) {
                        color = "red.400";
                        sign = "−";
                      } else if (ratio < 0.2) {
                        color = "yellow.400";
                        sign = "+";
                      } else {
                        color = "green.300"; // яркосалатовый
                        sign = "+";
                      }

                      return (
                        <Td textAlign="right" fontWeight="bold" color={color} py={pageStore.rowHeight}>
                          {sign}
                          {formatCellValue(Math.abs(percent), true)}
                        </Td>
                      );
                    })()}

                    {pageStore.part_column_payments && (
                      <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                        {formatCellValue(current / (totalSums[timeKey] || 0) * 100, true)}
                      </Td>
                    )}
                  </React.Fragment>
                );
              })}
            {availableColumns.map(item => item.key).includes("RightTotal") ?
              <Td textAlign="right" fontWeight="bold" py={pageStore.rowHeight}>
                {formatCellValue(subGroup.totalSum, true)}
              </Td>
              : <></>}
            {availableColumns.map(item => item.key).includes("RightAvgTotal") ?
              <Td textAlign="right" py={pageStore.rowHeight}>
                {formatCellValue(subGroupAvg, true)}
              </Td>
              : <></>}
          </Tr>
        )}

        {isExpanded && subGroup.items.map((item, idx) =>
          renderDetailRow(item, idx, subGroup.items.length)
        )}
      </React.Fragment>
    );
  };

  const renderDetailRow = (item, index, totalItems) => {
    const rowTimeKey = periodKeyOf(item.DateDoc, pageStore.year_or_mouth_view);

    return (
      <Tr key={index} bg={index % 2 === 0 ? "#555" : "#666"}>
        <Td px={4} py={pageStore.rowHeight} />
        <Td px={4} py={pageStore.rowHeight} />
        {availableColumns.map(item => item.key).includes("LeftTotal") ?
          <Td px={4} py={pageStore.rowHeight} textAlign="right" fontStyle={item.active ? "normal" : "italic"}>
            {formatCellValue(item.Sum, true)}
          </Td>
          : <></>}
        {availableColumns.map(item => item.key).includes("LeftAvgTotal") ?
          <Td px={4} py={pageStore.rowHeight} textAlign="right" fontStyle={item.active ? "normal" : "italic"}>
            {formatCellValue(allTimeKeys.length > 0 ? item.Sum / allTimeKeys.length : 0, true)}
          </Td>
          : <></>}
        {detailColumns
          .map(column => (
            <Td
              key={column.key}
              px={4}
              py={pageStore.rowHeight}
              textAlign={column.isNumeric ? "right" : "left"}
              fontStyle={item.active ? "normal" : "italic"}
              {...paymentTextAttributes(cellValue(column, item), column)}
            >
              {renderCellValue(column, cellValue(column, item))}
            </Td>
          ))}
        {pageStore.year_or_mouth_view && pageStore.absolute_column_payments && allTimeKeys.map(timeKey => (
          <Td key={timeKey} px={4} py={pageStore.rowHeight} textAlign="right" fontStyle={item.active ? "normal" : "italic"}>
            {timeKey === rowTimeKey ? formatCellValue(item.Sum, true) : "0"}
          </Td>
        ))}
        {pageStore.year_or_mouth_view && pageStore.percent_column_payments && allTimeKeys.map(timeKey => (
          <Td key={timeKey} px={4} py={pageStore.rowHeight} textAlign="right" fontStyle={item.active ? "normal" : "italic"}>
            {"-"}
          </Td>
        ))}
        {pageStore.year_or_mouth_view && pageStore.part_column_payments && allTimeKeys.map(timeKey => (
          <Td key={timeKey} px={4} py={pageStore.rowHeight} textAlign="right" fontStyle={item.active ? "normal" : "italic"}>
            {timeKey === rowTimeKey ? formatCellValue(item.Sum / (totalSums[timeKey] || 0) * 100, true) : "0"}
          </Td>
        ))}
        {availableColumns.map(item => item.key).includes("RightTotal") ?
          <Td px={4} py={pageStore.rowHeight} textAlign="right" fontStyle={item.active ? "normal" : "italic"}>
            {formatCellValue(item.Sum, true)}
          </Td>
          : <></>}
        {availableColumns.map(item => item.key).includes("RightAvgTotal") ?
          <Td px={4} py={pageStore.rowHeight} textAlign="right" fontStyle={item.active ? "normal" : "italic"}>
            {formatCellValue(allTimeKeys.length > 0 ? item.Sum / allTimeKeys.length : 0, true)}
          </Td>
          : <></>}
      </Tr>
    );
  };

  const renderFlatTable = () => (
    <Table tableLayout="fixed" width="100%" minWidth="800px" variant="simple" bg="" color="white">
      <Thead>
        <Tr>
          {availableColumns.map(column => (
            <Th
              key={column.key}
              color="white"
              onClick={() => handleSort(column.key, 'detail')}
              cursor="pointer"
              px={4}
              py={2}
              textAlign={column.isNumeric ? "right" : "left"}
            >
              {column.title}
              {sortConfig.key === column.key && sortConfig.level === 'detail' && (
                <Box as="span" ml={2}>
                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                </Box>
              )}
            </Th>
          ))}
        </Tr>
      </Thead>
      <Tbody>
        {data.map((row, index) => (
          <Tr key={index} bg={index % 2 === 0 ? "#444" : "#333"}>
            {availableColumns.map(column => (
              <Td
                key={column.key}
                px={4}
                py={pageStore.rowHeight}
                textAlign={column.isNumeric ? "right" : "left"}
                fontStyle={row.active ? "normal" : "italic"}
                {...paymentTextAttributes(cellValue(column, row), column)}
              >
                {renderCellValue(column, cellValue(column, row))}
              </Td>
            ))}
          </Tr>
        ))}
      </Tbody>
    </Table>
  );

  const buttons = (
    <>
      <Button onClick={handleExportToXLSX} bg="#333" color="white" width="15vw" _hover={{ bg: "#444" }} mr={3}>
        Экспорт в Excel
      </Button>
      <Button onClick={expandAll} bg="#333" color="white" width="15vw" _hover={{ bg: "#444" }} mr={3}>
        Развернуть всё
      </Button>
      <Button onClick={collapseAll} bg="#333" color="white" width="15vw" _hover={{ bg: "#444" }}>
        Свернуть всё
      </Button>
    </>
  )


  return (
    <>
     <Collapse in={pageStore.open_filter} animateOpacity>
      {renderButtons ? renderButtons(buttons) : buttons}
     </Collapse>

      <Box w="100%">
        {pageStore?.data_payments.length ? (
          <>
            <ColumnFilters />

            <Box
              maxHeight="95vh"
              overflowX="auto"
              borderRadius="md"
              borderWidth={1}
              borderColor="gray.300"
            >
              {groupedData ? (
                <Table
                  tableLayout="auto"
                  variant="simple"
                  color="white"
                  width="auto"
                  maxWidth="fit-content"
                >
                  {renderTableHeader()}
                  <Tbody>
                    {renderTotalRow()}
                    {sortedGroupedData?.map(renderGroupRow)}
                  </Tbody>
                </Table>
              ) : (
                renderFlatTable()
              )}
            </Box>
          </>
        ) : null}
      </Box>
    </>
  );
});

export default TableData;
